import {
  CSSProperties,
  ForwardRefRenderFunction,
  FunctionComponent,
  MouseEventHandler,
  forwardRef,
} from 'react';
import cn from 'classnames';
import { DraftConfigurationColor, PrintColor as PrintColorType, PrintType } from '@/types';
import { isBrightColor } from '@/utils/colors/isBrightColor';
import { isNumber } from '@/utils/isNumber';
import { getParams } from '@/utils/params';
import { Flex, Flock, SpecialFlex } from '@/model/printType/printTypes';
import { CheckMark } from '@/components/common/icons/CheckMark';
import './PrintColor.scss';

const SpriteMap: Record<string, Record<'EU' | 'NA', Record<string, number>>> = {
  [Flock]: {
    EU: {
      1: 0, // white,
      106: 1, // sand
      105: 2, // chocolate
      4: 3, // yellow gold
      8: 4, // orange
      5: 5, // red
      26: 6, // magenta
      24: 7, // purple
      74: 8, // navy
      23: 9, // light blue
      7: 10, // silver grey
      2: 11, // black
      6: 12, // green
    },
    NA: {
      1: 0, // white,
      105: 2, // chocolate
      4: 3, // yellow gold
      8: 4, // orange
      5: 5, // red
      26: 6, // magenta
      24: 7, // purple
      23: 9, // light blue
      7: 10, // silver grey
      2: 11, // black
      6: 12, // green
    },
  },
  [SpecialFlex]: {
    EU: {
      216: 0, // black
      94: 1, // blue
      93: 2, // gold
      217: 3, // green
      91: 4, // pink
      92: 5, // red
      95: 6, // silver
      218: 7, // white,
      90: 8, // glow in the dark
      73: 13, // reflective
    },
    NA: {
      140: 0, // black
      94: 1, // blue
      100: 2, // gold
      217: 3, // green
      137: 4, // pink
      99: 5, // red
      101: 6, // silver
      218: 7, // white,
      90: 8, // glow in the dark
      17: 9, // neon yellow
      162: 10, // neon orange
      83: 11, // neon pink
      75: 12, // neon green
    },
  },
  [Flex]: {
    EU: {
      71: 0, // silver-metallic,
      70: 1, // gold-metallic
    },
    NA: {
      71: 0, // metallic silver,
      70: 1, // metallic gold
    },
  },
};

const SpriteClasses: Record<string, string> = {
  [Flock]: 'print-color--flock',
  [SpecialFlex]: 'print-color--special-flex',
  [Flex]: 'print-color--realistic-flex',
};

const isPrintColor = (color: DraftConfigurationColor | PrintColorType): color is PrintColorType =>
  'lifeCycleState' in color;

interface Props {
  printType: PrintType;
  color: DraftConfigurationColor | PrintColorType;
  size?: number;
  disabled?: boolean;
}

export const PrintColor: FunctionComponent<Props> = ({ printType, color, size = 32, disabled }) => {
  if (!printType || !color) {
    return null;
  }

  let borderColor = 'transparent';
  if (disabled) {
    borderColor = '#b2b2b2';
  } else if (color.fill === '#FFFFFF') {
    borderColor = '#999';
  }

  const style: CSSProperties = {
    border: `1px solid ${borderColor}`,
    width: size,
    height: size,
  };

  const classNames = ['print-color'];

  if (isPrintColor(color)) {
    if (color.lifeCycleState === 'DEACTIVATED') {
      classNames.push('print-color--deactivated');
    } else {
      const spritePosition = SpriteMap[printType.id]?.[getParams().platform]?.[color.id];
      if (isNumber(spritePosition)) {
        style.backgroundSize = size + 2;
        style.backgroundPosition = `-1px -${(size + 2) * spritePosition + 1}px`;
        classNames.push(SpriteClasses[printType.id]);
      } else {
        style.backgroundColor = color.fill;
      }
    }
  } else {
    style.backgroundColor = color.fill;
  }

  return <div className={cn(classNames)} style={style} />;
};

interface PrintColorButtonProps extends Props {
  selected?: boolean;
  onClick?: MouseEventHandler;
}

const PrintColorButtonWithRef: ForwardRefRenderFunction<
  HTMLButtonElement,
  PrintColorButtonProps
> = ({ printType, color, selected, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    title={color.name}
    className={cn('print-color-button', {
      'print-color-button--selected': selected,
      'print-color-button--bright': isBrightColor(color.fill),
    })}
    onClick={onClick}
    disabled={isPrintColor(color) && color.lifeCycleState === 'DEACTIVATED'}
    role="option"
    aria-selected={selected}
  >
    <div className="print-color-wrapper" aria-selected={selected}>
      <PrintColor printType={printType} color={color} />
      {selected && <CheckMark width="2em" height="2em" />}
    </div>
  </button>
);

export const PrintColorButton = forwardRef<HTMLButtonElement, PrintColorButtonProps>(
  PrintColorButtonWithRef
);
